/**
 * Alpine.js Like Button Component
 *
 * Usage in Twig:
 * <div x-data="likeButton('post', '123', false, 5)">
 *   <button @click="toggleLike()">...</button>
 *   <span x-text="likeCount"></span>
 * </div>
 */

/**
 * Raw like API call for use by photo viewers and other components.
 * @param {string} entityType - 'photo', 'post', etc.
 * @param {string} entityId - Entity ID as string
 * @param {boolean} wasLiked - True if currently liked (will unlike)
 */
export async function toggleLikeApi(entityType, entityId, wasLiked) {
    const token = await window.getApiToken();
    const response = wasLiked
        ? await fetch(`/v1/like?${new URLSearchParams({ entity_type: entityType, entity_id: String(entityId) })}`, {
            method: 'DELETE',
            headers: { 'X-AUTH-TOKEN': token },
            credentials: 'include'
        })
        : await fetch('/v1/like', {
            method: 'POST',
            headers: { 'X-AUTH-TOKEN': token, 'Content-Type': 'application/x-www-form-urlencoded' },
            credentials: 'include',
            body: new URLSearchParams({ entity_type: entityType, entity_id: String(entityId) })
        });
    if (!response.ok) throw new Error(window.t ? window.t('common.request_failed') : 'Request failed');
}

export function likeButton(entityType, entityId, isLiked, likeCount) {
    return {
        entityType,
        entityId: String(entityId),
        isLiked,
        likeCount,
        isLoading: false,

        async toggleLike() {
            if (this.isLoading) return;
            this.isLoading = true;

            const wasLiked = this.isLiked;
            this.isLiked = !wasLiked;
            this.likeCount += wasLiked ? -1 : 1;

            try {
                await toggleLikeApi(this.entityType, this.entityId, wasLiked);
            } catch (error) { // eslint-disable-line no-unused-vars
                this.isLiked = wasLiked;
                this.likeCount += wasLiked ? 1 : -1;
                window.showError?.(window.t ? window.t('common.like_update_failed') : 'Failed to update like');
            } finally {
                this.isLoading = false;
            }
        },

        showLikers() {
            if (this.likeCount > 0) {
                window.dispatchEvent(new CustomEvent('open-likers-modal', {
                    detail: {
                        entityType: this.entityType,
                        entityId: this.entityId,
                        count: this.likeCount
                    }
                }));
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.likeButton = likeButton;
}

export default likeButton;
